import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Save, Eye, Send, Calendar, Copy, EyeOff, Trash2, ArrowLeft, FileText, Sparkles, Search, Share2, Settings2, Loader2, Wand2, Link2, Facebook, Twitter, Linkedin, Check } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import BlockEditor from "@/components/admin/BlockEditor";
import AiWriter from "@/components/admin/AiWriter";
import { ImageField } from "@/components/admin/MediaPicker";
import BlockRenderer from "@/components/public/BlockRenderer";

const inputCls = "w-full bg-ink-800 border border-ink-600 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-brand transition-colors text-white placeholder:text-slate-600";
const labelCls = "text-xs text-slate-400 font-mono-label";

const EMPTY = {
  title: "", subtitle: "", excerpt: "", blocks: [], category: "", tags: [], author: "", featured_image: "",
  featured_image_alt: "", slug: "", publish_date: "", seo_title: "", meta_description: "", keywords: [],
  canonical_url: "", social_headline: "", social_description: "", social_image: "", cta_id: "", affiliate_ids: [],
  status: "DRAFT", is_featured: false, is_trending: false, is_editors_pick: false, is_popular: false, no_index: false,
};

const TABS = [["content", "Content", FileText], ["ai", "AI Writer", Sparkles], ["seo", "SEO", Search], ["social", "Social", Share2], ["settings", "Settings", Settings2]];

function blocksToText(blocks) {
  return (blocks || []).map((b) => {
    if (b.type === "heading") return b.text;
    if (b.type === "text" || b.type === "html") return (b.html || "").replace(/<[^>]+>/g, " ");
    if (b.type === "list") return (b.items || []).join(". ");
    if (b.type === "quote" || b.type === "pullquote" || b.type === "callout") return b.text;
    return "";
  }).join("\n\n").trim();
}

export default function PostEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;
  const [post, setPost] = useState(EMPTY);
  const [tab, setTab] = useState("content");
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [cats, setCats] = useState([]);
  const [ctas, setCtas] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [schedule, setSchedule] = useState("");
  const [copied, setCopied] = useState(false);
  const [aiBusy, setAiBusy] = useState("");

  useEffect(() => {
    api.get("/admin/categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/admin/ctas").then((r) => setCtas(r.data)).catch(() => {});
    api.get("/admin/affiliates").then((r) => setAffiliates(r.data)).catch(() => {});
    if (!isNew) api.get(`/admin/posts/${id}`).then((r) => setPost({ ...EMPTY, ...r.data })).catch(() => { toast.error("Could not load post"); navigate("/admin/posts"); });
  }, [id]);

  const set = (patch) => setPost((p) => ({ ...p, ...patch }));

  const save = async (status) => {
    const body = { ...post };
    if (status) body.status = status;
    if (status === "SCHEDULED" && schedule) body.publish_date = new Date(schedule).toISOString();
    if (!body.title.trim()) { toast.error("Add a title first"); return null; }
    setSaving(true);
    try {
      let res;
      if (isNew) {
        res = await api.post("/admin/posts", body);
        toast.success("Saved");
        navigate(`/admin/posts/${res.data.id}`, { replace: true });
      } else {
        res = await api.put(`/admin/posts/${id}`, body);
        toast.success(status === "PUBLISHED" ? "Published" : "Saved");
      }
      setPost({ ...EMPTY, ...res.data });
      return res.data;
    } catch (e) { toast.error(apiErr(e)); return null; }
    finally { setSaving(false); }
  };

  const duplicate = async () => {
    if (isNew) return;
    try { const { data } = await api.post(`/admin/posts/${id}/duplicate`); toast.success("Duplicated"); navigate(`/admin/posts/${data.id}`); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async () => {
    if (isNew || !window.confirm("Delete this article permanently?")) return;
    try { await api.delete(`/admin/posts/${id}`); toast.success("Deleted"); navigate("/admin/posts"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const loadAiDraft = (data) => {
    const hasContent = post.blocks.length > 0 || post.title.trim();
    if (hasContent && !window.confirm("Load the generated draft? This replaces the current unsaved content in the editor.")) return;
    set({
      title: data.title || post.title,
      subtitle: data.subtitle || post.subtitle,
      excerpt: data.excerpt || post.excerpt,
      blocks: data.blocks || [],
      tags: data.tags || post.tags,
      seo_title: data.seo_title || "",
      meta_description: data.meta_description || "",
      keywords: data.keywords || [],
      social_headline: data.social_headline || "",
      social_description: data.social_description || "",
    });
    setTab("content");
  };

  const runAi = async (action, field) => {
    const text = blocksToText(post.blocks) || post.title;
    if (!text) { toast.error("Add some content first"); return; }
    setAiBusy(action);
    try {
      const { data } = await api.post("/admin/ai/action", { action, text });
      set({ [field]: data.result.replace(/<[^>]+>/g, "").trim() });
      toast.success("Generated");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setAiBusy(""); }
  };

  const copyLink = () => {
    const url = `${window.location.origin}/article/${post.slug}`;
    navigator.clipboard.writeText(url); setCopied(true); toast.success("Link copied"); setTimeout(() => setCopied(false), 2000);
  };

  const socialImg = post.social_image || post.featured_image;
  const publicUrl = `${window.location.origin}/article/${post.slug}`;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <button onClick={() => navigate("/admin/posts")} className="p-2 rounded-lg border border-ink-600 text-slate-400 hover:text-white"><ArrowLeft size={16} /></button>
        <div className="flex-1 min-w-[200px]">
          <input value={post.title} onChange={(e) => set({ title: e.target.value })} data-testid="post-title" placeholder="Untitled article" className="w-full bg-transparent text-2xl font-display font-extrabold text-white outline-none placeholder:text-slate-700" />
        </div>
        <span className={`text-[10px] font-mono-label px-2.5 py-1 rounded-full border ${post.status === "PUBLISHED" ? "text-brand border-brand/40 bg-brand/10" : "text-slate-400 border-ink-600"}`}>{post.status}</span>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        <button onClick={() => save("DRAFT")} disabled={saving} data-testid="save-draft" className="inline-flex items-center gap-1.5 text-sm font-semibold border border-ink-600 text-slate-200 px-4 py-2 rounded-full hover:border-brand transition-colors disabled:opacity-60"><Save size={15} /> Save Draft</button>
        <button onClick={() => setPreview(true)} data-testid="preview-btn" className="inline-flex items-center gap-1.5 text-sm font-semibold border border-ink-600 text-slate-200 px-4 py-2 rounded-full hover:border-brand transition-colors"><Eye size={15} /> Preview</button>
        <button onClick={() => save("PUBLISHED")} disabled={saving} data-testid="publish-btn" className="inline-flex items-center gap-1.5 text-sm font-bold bg-brand text-brand-dark px-4 py-2 rounded-full hover:bg-brand-neon transition-colors disabled:opacity-60">{saving ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} {isNew ? "Publish" : "Update & Publish"}</button>
        {!isNew && (
          <>
            <button onClick={duplicate} className="inline-flex items-center gap-1.5 text-sm font-semibold border border-ink-600 text-slate-200 px-4 py-2 rounded-full hover:border-brand transition-colors"><Copy size={15} /> Duplicate</button>
            {post.status === "PUBLISHED" && <button onClick={() => save("UNPUBLISHED")} className="inline-flex items-center gap-1.5 text-sm font-semibold border border-ink-600 text-slate-200 px-4 py-2 rounded-full hover:border-amber-400 transition-colors"><EyeOff size={15} /> Unpublish</button>}
            <button onClick={del} data-testid="delete-btn" className="inline-flex items-center gap-1.5 text-sm font-semibold border border-ink-600 text-red-400 px-4 py-2 rounded-full hover:border-red-400 transition-colors"><Trash2 size={15} /> Delete</button>
          </>
        )}
      </div>

      <div className="flex items-center gap-1 border-b border-ink-700 mb-6 overflow-x-auto thin-scroll">
        {TABS.map(([key, label, Icon]) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`tab-${key}`} className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${tab === key ? "border-brand text-brand" : "border-transparent text-slate-400 hover:text-white"}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {tab === "content" && (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div><label className={labelCls}>Subtitle / Deck</label><input value={post.subtitle} onChange={(e) => set({ subtitle: e.target.value })} className={`${inputCls} mt-1.5`} placeholder="A short line under the headline" /></div>
            <div>
              <div className="flex items-center justify-between">
                <label className={labelCls}>Excerpt</label>
                <button onClick={() => runAi("EXCERPT", "excerpt")} disabled={!!aiBusy} className="text-[11px] text-brand font-semibold inline-flex items-center gap-1">{aiBusy === "EXCERPT" ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />} AI excerpt</button>
              </div>
              <textarea value={post.excerpt} onChange={(e) => set({ excerpt: e.target.value })} rows={2} className={`${inputCls} mt-1.5`} placeholder="1 to 2 sentence summary" />
            </div>
            <div>
              <label className={labelCls}>Article body</label>
              <div className="mt-2"><BlockEditor blocks={post.blocks} onChange={(blocks) => set({ blocks })} /></div>
            </div>
          </div>
          <div className="space-y-4">
            <div className="rounded-xl border border-ink-600 bg-ink-900 p-4">
              <ImageField value={post.featured_image} onChange={(url) => set({ featured_image: url })} label="Featured image" testid="featured-image" />
              <div className="mt-3"><label className={labelCls}>Featured image alt text</label><input value={post.featured_image_alt} onChange={(e) => set({ featured_image_alt: e.target.value })} className={`${inputCls} mt-1.5`} placeholder="Describe the image" /></div>
            </div>
            <div className="rounded-xl border border-ink-600 bg-ink-900 p-4 space-y-3">
              <div><label className={labelCls}>Category</label>
                <select value={post.category} onChange={(e) => set({ category: e.target.value })} data-testid="post-category" className={`${inputCls} mt-1.5`}>
                  <option value="">Select category</option>
                  {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
              </div>
              <div><label className={labelCls}>Author</label><input value={post.author} onChange={(e) => set({ author: e.target.value })} className={`${inputCls} mt-1.5`} placeholder="Author name" /></div>
              <div><label className={labelCls}>Tags (comma separated)</label><input value={(post.tags || []).join(", ")} onChange={(e) => set({ tags: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className={`${inputCls} mt-1.5`} placeholder="ideas, ecommerce" /></div>
            </div>
          </div>
        </div>
      )}

      {tab === "ai" && (
        <div className="max-w-2xl"><AiWriter onGenerated={loadAiDraft} /></div>
      )}

      {tab === "seo" && (
        <div className="max-w-2xl space-y-4">
          <div><label className={labelCls}>URL slug</label><input value={post.slug} onChange={(e) => set({ slug: e.target.value })} data-testid="post-slug" className={`${inputCls} mt-1.5`} placeholder="auto-generated-from-title" /></div>
          <div><label className={labelCls}>SEO title</label><input value={post.seo_title} onChange={(e) => set({ seo_title: e.target.value })} className={`${inputCls} mt-1.5`} placeholder="Under 60 characters" /></div>
          <div>
            <div className="flex items-center justify-between"><label className={labelCls}>Meta description</label><button onClick={() => runAi("SEO_DESCRIPTION", "meta_description")} disabled={!!aiBusy} className="text-[11px] text-brand font-semibold inline-flex items-center gap-1">{aiBusy === "SEO_DESCRIPTION" ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />} AI generate</button></div>
            <textarea value={post.meta_description} onChange={(e) => set({ meta_description: e.target.value })} rows={2} className={`${inputCls} mt-1.5`} placeholder="Under 155 characters" />
          </div>
          <div><label className={labelCls}>Keywords (comma separated)</label><input value={(post.keywords || []).join(", ")} onChange={(e) => set({ keywords: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className={`${inputCls} mt-1.5`} /></div>
          <div><label className={labelCls}>Canonical URL</label><input value={post.canonical_url} onChange={(e) => set({ canonical_url: e.target.value })} className={`${inputCls} mt-1.5`} placeholder="Leave blank to use default" /></div>
          <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer"><input type="checkbox" checked={post.no_index} onChange={(e) => set({ no_index: e.target.checked })} className="accent-emerald-500" /> Hide from search engines (noindex)</label>
        </div>
      )}

      {tab === "social" && (
        <div className="grid lg:grid-cols-2 gap-8 max-w-5xl">
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between"><label className={labelCls}>Social headline</label><button onClick={() => runAi("SOCIAL_CAPTION", "social_headline")} disabled={!!aiBusy} className="text-[11px] text-brand font-semibold inline-flex items-center gap-1">{aiBusy === "SOCIAL_CAPTION" ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />} AI caption</button></div>
              <input value={post.social_headline} onChange={(e) => set({ social_headline: e.target.value })} className={`${inputCls} mt-1.5`} placeholder="Can differ from article headline" />
            </div>
            <div><label className={labelCls}>Social description</label><textarea value={post.social_description} onChange={(e) => set({ social_description: e.target.value })} rows={2} className={`${inputCls} mt-1.5`} /></div>
            <ImageField value={post.social_image} onChange={(url) => set({ social_image: url })} label="Social image (falls back to featured)" />
            <div className="flex flex-wrap gap-2 pt-2">
              <button onClick={copyLink} className="inline-flex items-center gap-1.5 text-xs font-semibold border border-ink-600 text-slate-200 px-3 py-2 rounded-full hover:border-brand transition-colors">{copied ? <Check size={13} /> : <Link2 size={13} />} Copy link</button>
              <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(publicUrl)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold border border-ink-600 text-slate-200 px-3 py-2 rounded-full hover:border-brand transition-colors"><Facebook size={13} /> Facebook</a>
              <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(publicUrl)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold border border-ink-600 text-slate-200 px-3 py-2 rounded-full hover:border-brand transition-colors"><Twitter size={13} /> X</a>
              <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(publicUrl)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold border border-ink-600 text-slate-200 px-3 py-2 rounded-full hover:border-brand transition-colors"><Linkedin size={13} /> LinkedIn</a>
            </div>
          </div>
          <div>
            <div className="label-mono mb-3">Card preview</div>
            <div className="rounded-xl overflow-hidden border border-ink-600 bg-ink-800 max-w-md">
              <div className="h-52 bg-ink-700 overflow-hidden">{socialImg ? <img src={socialImg} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-600 text-sm">No image set</div>}</div>
              <div className="p-4">
                <div className="text-[11px] text-slate-500 uppercase tracking-wide">lucrativelaptop.com</div>
                <div className="font-semibold text-white mt-1 line-clamp-2">{post.social_headline || post.seo_title || post.title || "Your headline appears here"}</div>
                <div className="text-sm text-slate-400 mt-1 line-clamp-2">{post.social_description || post.meta_description || post.excerpt || "Your description appears here"}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "settings" && (
        <div className="max-w-2xl space-y-5">
          <div className="rounded-xl border border-ink-600 bg-ink-900 p-5">
            <div className="label-mono mb-3">Homepage placement</div>
            <div className="grid grid-cols-2 gap-3">
              {[["is_featured", "Featured Story"], ["is_trending", "Trending"], ["is_editors_pick", "Editor's Pick"], ["is_popular", "Popular This Week"]].map(([k, l]) => (
                <label key={k} className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer"><input type="checkbox" checked={post[k]} onChange={(e) => set({ [k]: e.target.checked })} className="accent-emerald-500" /> {l}</label>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-ink-600 bg-ink-900 p-5 space-y-3">
            <div className="label-mono">Call to action & tools</div>
            <div><label className={labelCls}>Attach a CTA block</label>
              <select value={post.cta_id} onChange={(e) => set({ cta_id: e.target.value })} className={`${inputCls} mt-1.5`}>
                <option value="">None</option>
                {ctas.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div><label className={labelCls}>Recommended tools (affiliates)</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {affiliates.length === 0 && <span className="text-xs text-slate-500">No affiliate resources yet. Add them in CTAs & Affiliates.</span>}
                {affiliates.map((a) => {
                  const on = post.affiliate_ids?.includes(a.id);
                  return <button key={a.id} type="button" onClick={() => set({ affiliate_ids: on ? post.affiliate_ids.filter((x) => x !== a.id) : [...(post.affiliate_ids || []), a.id] })} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-brand/10 border-brand/40 text-brand" : "border-ink-600 text-slate-300 hover:border-brand"}`}>{a.name}</button>;
                })}
              </div>
            </div>
          </div>
          <div className="rounded-xl border border-ink-600 bg-ink-900 p-5 space-y-3">
            <div className="label-mono">Schedule</div>
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[200px]"><label className={labelCls}>Publish date & time</label><input type="datetime-local" value={schedule} onChange={(e) => setSchedule(e.target.value)} className={`${inputCls} mt-1.5`} /></div>
              <button onClick={() => save("SCHEDULED")} disabled={saving || !schedule} className="inline-flex items-center gap-1.5 text-sm font-bold bg-brand text-brand-dark px-4 py-2.5 rounded-full hover:bg-brand-neon transition-colors disabled:opacity-50"><Calendar size={15} /> Schedule</button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={preview} onOpenChange={setPreview}>
        <DialogContent className="bg-ink-950 border-ink-600 text-white max-w-3xl max-h-[88vh] overflow-y-auto thin-scroll">
          <div className="pt-2">
            {post.category && <div className="label-mono mb-3">{post.category.replace(/-/g, " ")}</div>}
            <h1 className="font-display font-extrabold text-white text-3xl leading-tight">{post.title || "Untitled"}</h1>
            {post.subtitle && <p className="text-slate-300 text-lg mt-3">{post.subtitle}</p>}
            {post.featured_image && <img src={post.featured_image} alt={post.featured_image_alt} className="w-full rounded-xl border border-ink-600 mt-6" />}
            <div className="mt-6"><BlockRenderer blocks={post.blocks} postId={post.id} /></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
